import { getAuth } from 'firebase/auth';
import { useContext } from 'react';
import AuthContext from '@/contexts/auth';
import SchoolContext from '@/contexts/school';
import { useFirebaseAuth } from '@/hooks/useFirebaseAuth';
import { axiosInstance as axios } from '@/plugins/axios';
import firebase from '@/plugins/firebaseConfig';
import { Member } from '@/types/Member';

export type CreateAdminUserParams = {
  email: string;
  password: string;
  name: string;
  schoolRole: number | null;
  courtRentalRole: number | null;
  tournamentRole: number | null;
  schoolName: string;
};

export const useFirebaseAuthUserAdmin = () => {
  const { tenant_id, schema, slug } = useContext(SchoolContext);
  const { accessToken } = useContext(AuthContext);

  const auth = getAuth(firebase);
  const { sendPwResetEmail } = useFirebaseAuth();

  auth.tenantId = tenant_id;
  auth.languageCode = 'ja';

  const apiPath = process.env.NEXT_PUBLIC_FUNCTIONS_URL;

  // Hasura Role Config START
  const hasuraDefaultRoles = 'anonymous';
  const hasuraAllowRoles = ['staff'];
  // Hasura Role Config END

  /**
   * Create admin user
   * @param email
   * @param password
   * @param name
   * @param role
   * @param schoolName
   * @param tournamentIds
   */
  const createAdminUser = async ({
    name,
    email,
    password,
    schoolName,
    schoolRole,
    tournamentRole,
    courtRentalRole,
  }: CreateAdminUserParams) => {
    const endpoint = `${process.env.NEXT_PUBLIC_RESERVE_ENDPOINT}/api/v1/auth/createSchoolAdmin?schema=${schema}&slug=${slug}`;
    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

    const body = {
      email,
      password,
      tenantId: tenant_id,
      defaultRole: hasuraDefaultRoles,
      allowRoles: hasuraAllowRoles,
      schoolName,
      schoolRole,
      tournamentRole,
      courtRentalRole,
      name,
    };

    try {
      const createUserRes = (await axios.post(endpoint, body)).data;

      if (createUserRes.isSuccess) {
        // Send email
        await sendPwResetEmail(email, tenant_id);
      }

      return createUserRes;
    } catch (error) {
      console.log('🚀 ~ file: useFirebaseAuthUserAdmin.ts:125 ~ useFirebaseAuthUserAdmin ~ error:', error);
      throw error;
    }
  };

  /**
   * Delete admin member
   * @param email
   * @returns
   */
  const deleteAdminMember = async (email: string) => {
    if (email) {
      const endpoint = `${process.env.NEXT_PUBLIC_RESERVE_ENDPOINT}/api/v1/auth/deleteMember?schema=${schema}&slug=${slug}`;
      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      const response = await axios.delete(endpoint, { data: { email, tenantId: tenant_id } });
      return response.data;
    } else {
      throw new Error('削除できません');
    }
  };

  /**
   * Create Firebase User CustomClaims
   * @param uid
   * @param tenant_id
   * @returns {isSuccess: boolean, message: string}
   */
  const setFbAuthCustomClaims = async (uid: string, tenant_id: string) => {
    const createData = {
      uid: uid,
      tenantId: tenant_id,
      defaultRole: hasuraDefaultRoles,
      allowRoles: hasuraAllowRoles,
    };

    const res = await fetch(apiPath + '/setCustomClaims', {
      headers: {
        'Content-Type': 'application/json;charset=utf-8',
      },
      method: 'POST',
      body: JSON.stringify(createData),
    });

    return res;
  };

  /**
   * Create member by admin
   * @param member type of Member
   * @returns
   */
  const createMemberByAdmin = async (member: Member) => {
    try {
      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

      const endpoint = `${process.env.NEXT_PUBLIC_RESERVE_ENDPOINT}/api/v1/auth/createMemberByAdmin?slug=${slug}&schema=${schema}`;
      const tenantId = String(process.env.NEXT_PUBLIC_TENANT_ID_MEMBER);

      /**
       * Register member
       */
      const body = {
        tenantId: tenantId,
        password: member.password,
        email: member.email,
        firstName: member.first_name,
        lastName: member.last_name,
        firstKana: member.first_kana,
        lastKana: member.last_kana,
        mobilePhone: member.mobile_phone,
        birthday: member.birthday,
        createByAdmin: true,
      };
      const registerRes = (await axios.post(endpoint, body)).data;

      if (!registerRes.isSuccess) {
        throw Error(registerRes.message);
      }

      return {
        isSuccess: true,
      };
    } catch (error: any) {
      return {
        isSuccess: false,
        message: error.message,
      };
    }
  };

  return {
    hasuraDefaultRoles,
    hasuraAllowRoles,
    setFbAuthCustomClaims,
    createAdminUser,
    deleteAdminMember,
    createMemberByAdmin,
  };
};
